import React, { useState, useEffect } from 'react';
import { X, Menu, ChevronUp } from 'lucide-react';
import { Link, Events, scrollSpy, scroller } from 'react-scroll';

const Navbar = ({ reduceMotion, setReduceMotion }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const [activeLink, setActiveLink] = useState('about');

  const links = [
    { id: 'about', label: 'About' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'projects', label: 'Projects' },
    { id: 'contact', label: 'Contact' }
  ];

  useEffect(() => {
    Events.scrollEvent.register('begin', () => {});
    Events.scrollEvent.register('end', () => {});
    scrollSpy.update();

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      setShouldShowButton(currentScrollY > 400);

      if ((windowHeight + currentScrollY) >= documentHeight - 10) {
        setActiveLink('contact');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      Events.scrollEvent.remove('begin');
      Events.scrollEvent.remove('end');
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    scroller.scrollTo('about', {
      duration: 800,
      delay: 0,
      smooth: 'easeInOutQuart'
    });
  };

  const handleLinkClick = (id) => {
    setIsMobileMenuOpen(false);
    setActiveLink(id);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      {/* Desktop Header */}
      <header className="hidden md:block fixed top-0 left-0 right-0 h-20 bg-[#1e1e1e] border-b-4 border-black z-50 shadow-md">
        <div className="max-w-6xl mx-auto h-full px-6 flex items-center justify-between">
          {/* Minecraft Green Title */}
          <div className="font-sans text-xl tracking-wider text-white font-extrabold uppercase select-none">
            <span className="text-[#55ff55]">I</span>am
            <span className="text-[#55ff55]">T</span>he
            <span className="text-[#55ff55]">D</span>efender
          </div>
          
          <nav className="flex items-center gap-4">
            <ul className="flex items-center gap-2">
              {links.map(({ id, label }) => (
                <li key={id}>
                  <Link
                    to={id}
                    spy={true}
                    smooth={true}
                    duration={800}
                    activeClass="active"
                    className={`mc-btn text-base font-bold uppercase tracking-wider transition-all cursor-pointer py-1.5 px-4 block
                      ${activeLink === id 
                        ? 'bg-[#5c5c5c] text-[#55ff55] border-[#55ff55] shadow-[inset_-2px_-2px_0px_0px_#111,inset_2px_2px_0px_0px_#55ff55]' 
                        : ''
                      }`}
                    onSetActive={() => setActiveLink(id)}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Reduced Motion Toggle Button */}
            <button
              onClick={() => setReduceMotion(!reduceMotion)}
              className={`mc-btn text-sm font-bold uppercase tracking-wider transition-all cursor-pointer py-1 px-3 flex items-center gap-2 ${
                reduceMotion 
                  ? 'bg-[#5c5c5c] text-[#ff5555] border-[#ff5555] shadow-[inset_-2px_-2px_0px_0px_#111,inset_2px_2px_0px_0px_#ff5555]' 
                  : 'text-[#55ff55] border-[#55ff55] shadow-[inset_-2px_-2px_0px_0px_#111,inset_2px_2px_0px_0px_#55ff55]'
              }`}
              title={reduceMotion ? "Enable animations" : "Disable animations"}
            >
              <span className={`inline-block w-2 h-2 border border-black ${reduceMotion ? 'bg-[#ff5555]' : 'bg-[#55ff55]'}`} />
              <span>{reduceMotion ? 'Motion: OFF' : 'Motion: ON'}</span>
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Navbar Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-16 bg-[#1e1e1e] border-b-4 border-black z-50 flex items-center justify-between px-4 shadow-md">
        <div className="text-white font-extrabold uppercase select-none">
          <span className="text-[#55ff55]">I</span>am
          <span className="text-[#55ff55]">T</span>he
          <span className="text-[#55ff55]">D</span>efender
        </div>
        <button
          onClick={toggleMobileMenu}
          className="p-1 rounded-md bg-[#8b8b8b] border-2 border-black shadow-[inset_-2px_-2px_0px_0px_#555,inset_2px_2px_0px_0px_#dbdbdb] text-white"
        >
          <Menu className="w-5 h-5" />
        </button>
      </header>

      {/* Mobile Menu Panel */}
      <div className={`md:hidden fixed inset-0 z-50 transition-all duration-300 ease-in-out
        ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-[#000000]/75"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Panel (Minecraft Bezel panel) */}
        <div className={`absolute top-0 right-0 bottom-0 w-64 bg-[#2e2e2e] border-l-4 border-black flex flex-col transform transition-transform duration-300 ease-in-out shadow-2xl
          ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          
          <div className="flex items-center justify-between p-6 border-b-4 border-black bg-[#1e1e1e]">
            <span className="text-lg font-bold text-white uppercase tracking-wider">Menu</span>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-1 rounded-md bg-[#8b8b8b] border-2 border-black shadow-[inset_-2px_-2px_0px_0px_#555,inset_2px_2px_0px_0px_#dbdbdb] text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <nav className="p-4 flex-grow overflow-y-auto">
            <ul className="space-y-2">
              {links.map(({ id, label }) => (
                <li key={id}>
                  <Link
                    to={id}
                    spy={true}
                    smooth={true}
                    offset={-70}
                    duration={800}
                    activeClass="active"
                    onClick={() => handleLinkClick(id)}
                    onSetActive={() => setActiveLink(id)}
                    className={`mc-btn block w-full text-center py-2 text-lg font-bold uppercase tracking-wider
                      ${activeLink === id 
                        ? 'bg-[#5c5c5c] text-[#55ff55] border-[#55ff55] shadow-[inset_-2px_-2px_0px_0px_#111,inset_2px_2px_0px_0px_#55ff55]' 
                        : ''
                      }`}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Mobile Animation Toggle */}
          <div className="p-4 border-t-4 border-black bg-[#1e1e1e]">
            <button
              onClick={() => setReduceMotion(!reduceMotion)}
              className={`mc-btn w-full text-center py-2 text-md font-bold uppercase tracking-wider flex items-center justify-center gap-2 ${
                reduceMotion
                  ? 'bg-[#5c5c5c] text-[#ff5555] border-[#ff5555] shadow-[inset_-2px_-2px_0px_0px_#111,inset_2px_2px_0px_0px_#ff5555]'
                  : 'text-[#55ff55] border-[#55ff55] shadow-[inset_-2px_-2px_0px_0px_#111,inset_2px_2px_0px_0px_#55ff55]'
              }`}
            >
              <span className={`inline-block w-2.5 h-2.5 border border-black ${reduceMotion ? 'bg-[#ff5555]' : 'bg-[#55ff55]'}`} />
              <span>{reduceMotion ? 'Motion: OFF' : 'Motion: ON'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-6 left-6 p-1 bg-[#8b8b8b] border-2 border-black shadow-[inset_-2px_-2px_0px_0px_#555,inset_2px_2px_0px_0px_#dbdbdb] text-white transition-all duration-200 z-50
          ${shouldShowButton ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
      >
        <ChevronUp className="w-5 h-5" />
      </button>
    </>
  );
};

export default Navbar;